/**
 * @file   src\components\TimeSettingsModal.tsx
 * @brief  Time Settings Modal component.
 * @date   Jun, 2024
 * @author ZCO Engineer
 * @copyright (c) 2024, ZCO
 */
import { useState, Modal, Button, useParams } from '../utils/thirdpartyComponents';
import CWSelect from './CWSelect';
import '../components/CWModal.scss';
import { BayTiminginterface, Timings } from '../interfaces/generalInterface';
import { FormEvent, useEffect } from 'react';
import axios from '../config/axiosConfig';
import { EquipmentApi, LocationApi } from '../utils/apiUrls';
import { MessageToaster } from '../utils/toastUtil';
import SmallLoader from './SmallLoader';
import * as Yup from 'yup'
import { TIME_SETTING_SCHEMA } from '../validations/timeSettingSchema';
import { CommonMessages } from '../messages/validationMessages';
import CWCheckbox from './CWCheckbox';

// Toast object creation.

const TimeSettingsModal = (props: any) => {
    const { show, onHide, bayId, carwash_owner } = props;
    const toaster = new MessageToaster();
    const [loading, setLoading] = useState(true)
    const daysOfWeek = [
        { label: "Sunday", value: 1 },
        { label: "Monday", value: 2 },
        { label: "Tuesday", value: 3 },
        { label: "Wednesday", value: 4 },
        { label: "Thursday", value: 5 },
        { label: "Friday", value: 6 },
        { label: "Saturday", value: 7 },
    ];

    const [options, setOptions] = useState([
        { label: '12:00 AM', value: '00:00' },
        { label: '12:30 AM', value: '00:30' },
        { label: '01:00 AM', value: '01:00' },
        { label: '01:30 AM', value: '01:30' },
        { label: '02:00 AM', value: '02:00' },
        { label: '02:30 AM', value: '02:30' },
        { label: '03:00 AM', value: '03:00' },
        { label: '03:30 AM', value: '03:30' },
        { label: '04:00 AM', value: '04:00' },
        { label: '04:30 AM', value: '04:30' },
        { label: '05:00 AM', value: '05:00' },
        { label: '05:30 AM', value: '05:30' },
        { label: '06:00 AM', value: '06:00' },
        { label: '06:30 AM', value: '06:30' },
        { label: '07:00 AM', value: '07:00' },
        { label: '07:30 AM', value: '07:30' },
        { label: '08:00 AM', value: '08:00' },
        { label: '08:30 AM', value: '08:30' },
        { label: '09:00 AM', value: '09:00' },
        { label: '09:30 AM', value: '09:30' },
        { label: '10:00 AM', value: '10:00' },
        { label: '10:30 AM', value: '10:30' },
        { label: '11:00 AM', value: '11:00' },
        { label: '11:30 AM', value: '11:30' },
        { label: '12:00 PM', value: '12:00' },
        { label: '12:30 PM', value: '12:30' },
        { label: '01:00 PM', value: '13:00' },
        { label: '01:30 PM', value: '13:30' },
        { label: '02:00 PM', value: '14:00' },
        { label: '02:30 PM', value: '14:30' },
        { label: '03:00 PM', value: '15:00' },
        { label: '03:30 PM', value: '15:30' },
        { label: '04:00 PM', value: '16:00' },
        { label: '04:30 PM', value: '16:30' },
        { label: '05:00 PM', value: '17:00' },
        { label: '05:30 PM', value: '17:30' },
        { label: '06:00 PM', value: '18:00' },
        { label: '06:30 PM', value: '18:30' },
        { label: '07:00 PM', value: '19:00' },
        { label: '07:30 PM', value: '19:30' },
        { label: '08:00 PM', value: '20:00' },
        { label: '08:30 PM', value: '20:30' },
        { label: '09:00 PM', value: '21:00' },
        { label: '09:30 PM', value: '21:30' },
        { label: '10:00 PM', value: '22:00' },
        { label: '10:30 PM', value: '22:30' },
        { label: '11:00 PM', value: '23:00' },
        { label: '11:30 PM', value: '23:30' },
        { label: '', value: '' }
    ]);

    const [errors, setErrors] = useState<Record<number, { startTime: string; endTime: string }>>({});

    const [timings, setTimings] = useState<Timings[]>([])

    // Temporary state for unsaved changes
    const [tempTimings, setTempTimings] = useState<Timings[]>([]);

    useEffect(() => {
        if (bayId)
            getBayTiming()
    }, [bayId])

    useEffect(() => {
        if (show) {
            setTempTimings([...timings]); // initialize tempTimings when modal opens
        }
    }, [show, timings]);

    // To get location timing
    const getBayTiming = async () => {
        try {
            const { data } = (!carwash_owner)
                ? await axios.get(EquipmentApi.GET_BAY_TIMING + bayId)
                : await axios.get(EquipmentApi.CARWASH_OWNER_GET_BAY_TIMING + bayId);
            if (data?.error === 0) {
                if (data.data.length) {
                    const dayTimings: BayTiminginterface[] = data.data
                    const transformedArray: Timings[] = dayTimings.map(item => ({
                        day: item.dayId,
                        startTime: item.startTime.slice(0, -3),// To remove seconds part from time
                        endTime: item.endTime.slice(0, -3),
                        isClosed: item.isClosed
                    }));

                    setTimings(transformedArray);
                } else {
                    const days = [1, 2, 3, 4, 5, 6, 7]
                    const initialTiming = days.map((day) => ({
                        day,
                        startTime: '08:00',
                        endTime: '19:00',
                        isClosed: false,
                    }));

                    setTimings(initialTiming)
                }
            }
            setLoading(false)

        } catch (error: any) {
            setLoading(false)
            throw new Error(error.response?.data?.message || error.message);
        }
    }

    const handleOnHide = () => {
        onHide();
    }


    // Function to validate time
    const validateTime = (startTime: string, endTime: string) => {
        if (startTime !== '' && endTime !== '') {
            // Convert time (HH:MM) to minutes 
            const toMinutes = (time: string) => {
                const [hours, minutes] = time.split(":").map(Number);
                return hours * 60 + minutes;
            };

            const startMinutes = toMinutes(startTime);
            const endMinutes = endTime ? toMinutes(endTime) : '';

            if (startMinutes && endMinutes)
                // startTime should be less than endTime
                if (startMinutes >= endMinutes) {
                    return false; // Invalid
                }
            return true; // Valid
        }
    };


    // On change of time
    const handleChange = (day: number, type: "start" | "end", value: string) => {

        const dayUpdated = tempTimings.find((timing) => timing.day === day);
        const startTime = type === "start" ? value : dayUpdated?.startTime || "08:00";
        const endTime = type === "end" ? value : dayUpdated?.endTime || "19:00";
        const isClosed = dayUpdated?.isClosed || false

        // Validate startTime is earlier than endtime
        const isValid = validateTime(startTime, endTime);

        // Validation required only if bay not closed
        if (!isClosed) {
            // To display errors
            setErrors((prevErrors) => ({
                ...prevErrors,
                [day]: {
                    startTime: type === "start" && !isValid ? CommonMessages.start_time_invalid : "",
                    endTime: type === "end" && !isValid ? CommonMessages.end_time_invalid : "",
                },
            }));
        }

        setTempTimings((prevTimings) => {

            const updatedDay = prevTimings.find((timing) => timing.day === day);

            if (updatedDay) {
                return prevTimings.map((timing) =>
                    timing.day === day ?
                        (
                            type === "end" ?
                                { ...timing, endTime: value }
                                : { ...timing, startTime: value }
                        ) : timing
                );
            } else {
                return [...prevTimings, { day, startTime, endTime, isClosed }];
            }
        });
    };

    // To get already saved time for each day
    const getTimingForDay = (dayValue: number) => {
        const timing = tempTimings.find((timing) => timing.day === dayValue);
        return {
            startTime: timing ? timing.startTime : "08:00",// 08:00am by default
            endTime: timing ? timing.endTime : "19:00",// 07:00 pm by default
            closed: timing ? timing.isClosed : false
        };
    };

    // To handle closed checkbox
    const handleClosed = (e: React.ChangeEvent<HTMLInputElement>, day: number) => {

        setTempTimings((prevTimings) => {

            const updatedDay = prevTimings.find((timing) => timing.day === day);
            const startTime = e.target.checked === true ? '' : (updatedDay?.startTime || "08:00");
            const endTime = e.target.checked === true ? '' : (updatedDay?.endTime || "19:00");
            const isClosed = e.target.checked

            if (updatedDay) {
                return prevTimings.map((timing) =>
                    timing.day === day ?

                        { ...timing, startTime: startTime, endTime: endTime, isClosed: e.target.checked }
                        : timing
                );
            } else {
                return [...prevTimings, { day, startTime, endTime, isClosed }];
            }
        });
    };

    // Update timings 
    const saveTime = async (e: FormEvent) => {
        e.preventDefault()
        try {
            // Validate form data
            await TIME_SETTING_SCHEMA.validate(tempTimings, { abortEarly: false });

            try {
                setLoading(true)
                const updatedTimingData = {
                    bayId: bayId,
                    dayTimings: tempTimings
                }

                const { data } = !carwash_owner
                    ? await axios.post(EquipmentApi.UPDATE_BAY_TIMING, updatedTimingData)
                    : await axios.post(EquipmentApi.CARWASH_OWNER_UPDATE_BAY_TIMING, updatedTimingData);
                if (data?.error === 0) {
                    toaster.toastSuccess(data?.message)
                    setTimings([...tempTimings]);
                    // Close modal
                    onHide()
                }
                else {
                    // show error message
                    toaster.toastError(data?.message);
                }
            }
            catch (error: any) {
                throw new Error(error?.response?.data?.message || error?.message);
            }
            finally {
                setLoading(false); // Hide loader
            }
        }
        catch (error) {
            if (error instanceof Yup.ValidationError) {
                const newErrors: Record<number, { startTime: string; endTime: string }> = {};

                error.inner.forEach((err) => {

                    const path = err.path || '';
                    const field = path.split('.')[1]; // Extract the field name

                    // Extract the index of the array 
                    const dayIndex = parseInt(path.split('[')[1]?.split(']')[0] || '');

                    const day = tempTimings[dayIndex]?.day;
                    if (day !== undefined && field) {
                        newErrors[day] = {
                            ...newErrors[day],
                            [field]: err.message, // Set the error message for the field
                        };
                    }
                });

                setErrors(newErrors); // Update errors 
            }

        }
    }



    return (
        <Modal show={show} onHide={handleOnHide} centered className='time-modal'>
            <Modal.Body>
                <h5 className='f-bold'>Tunnel Hours</h5>
                <form onSubmit={saveTime}>
                    <div className="timing-wrap">
                        {daysOfWeek.map((day) => {
                            const { startTime, endTime, closed } = getTimingForDay(day.value);

                            return (
                                <div key={day.value} className="weekly-timer">
                                    <p className='f-semi'>{day.label}</p>
                                    <div className="timer-group d-flex justify-content-center">
                                        <CWSelect
                                            id='startTime'
                                            name='startTime'
                                            options={options}
                                            dropdownGap={0}
                                            dropdownPosition="auto"
                                            multi={false}
                                            className='hide-input'
                                            values={options.filter(option => option.value === startTime)}
                                            onChange={(time) => handleChange(day.value, "start", time[0].value)}
                                            errorMessage={errors[day.value]?.startTime || ""}
                                            disabled={closed}
                                        />
                                        <span className='to'>To</span>
                                        <CWSelect
                                            id='endTime'
                                            name='endTime'
                                            value={endTime}
                                            options={options}
                                            dropdownGap={0}
                                            dropdownPosition="auto"
                                            multi={false}
                                            className='hide-input'
                                            values={options.filter(option => option.value === endTime)}
                                            onChange={(time) => handleChange(day.value, "end", time[0].value)}
                                            errorMessage={errors[day.value]?.endTime || ""}
                                            disabled={closed}
                                        />
                                    </div>
                                    <CWCheckbox
                                        id="closed"
                                        name="closed"
                                        label="Closed"
                                        checked={closed}
                                        onChange={(e) => handleClosed(e, day.value)}
                                    />
                                </div>
                            )
                        })}


                    </div>

                    <div className="btn-sec d-flex">
                        <Button variant="secondary" className='secondary-btn' onClick={handleOnHide}>Cancel</Button>
                        <Button variant="primary" className='primary-btn' type='submit'>Save</Button>
                    </div>
                </form>
            </Modal.Body>

            {loading && <SmallLoader />}
        </Modal>
    );
};
export default TimeSettingsModal
